import { toIntOrNull } from '@utils/parse';
import { useApiModel } from '@hooks/use-api-model';
import { useApiSubmit } from '@hooks/use-api-submit';
import { DashRegistrationStep } from '@/types/dashboard';
import { defaultHistoryProgress } from '@/types/history/history-progress';
import type {
    IHistoryFamilyFormValues,
    IHistoryFamilyModel,
    IHistoryFamilySubmitResult,
} from '@/types/history/history-family';

const HISTORY_FAMILY_ENDPOINT = '/api/register/history-family';

const defaultHistoryFamilyModel: IHistoryFamilyModel = {
    ...defaultHistoryProgress,
    currentRegistrationStep: DashRegistrationStep.HistoryFamily,

    isLiverRegistration: false,

    yesValue: 0,

    familyHistoryKidneyDisease: null,
    familyMembersKidneyDisease: '',
    familyHistoryLiverDisease: null,
    familyMembersLiverDisease: '',
    familyHistoryBowelIssues: null,
    familyMembersBowelIssues: '',
    familyHistoryLupusLiver: null,
    familyMembersLupusLiver: '',
    familyHistoryHeartDisease: null,
    familyMembersHeartDisease: '',
    familyHistoryCad: null,
    familyMembersCad: '',
    familyHistoryBloodPressure: null,
    familyMembersBloodPressure: '',
    familyHistoryBloodDisorder: null,
    familyMembersBloodDisorder: '',
    familyHistoryCancer: null,
    familyMembersCancer: '',
    familyHistoryKidneyCancer: null,
    familyMembersKidneyCancer: '',
    familyHistoryDiabetes: null,
    familyMembersDiabetes: '',
    familyMemberSuddenDeathUnder50: null,
    familyMemberSuddenDeathUnder50Description: '',

    yesNoUnknownOptions: [],
};

export const useHistoryFamily = (registrationId: number, editKey: string) => {
    const url = `${HISTORY_FAMILY_ENDPOINT}/${registrationId}/${editKey}`;

    const { model, isLoading, error } = useApiModel(url, defaultHistoryFamilyModel);

    const i = toIntOrNull;

    const { submit, isSubmitting, submitError } = useApiSubmit<IHistoryFamilyFormValues, DashRegistrationStep, IHistoryFamilySubmitResult>(
        url,
        (values) => ({
            familyHistoryKidneyDisease: i(values.familyHistoryKidneyDisease),
            familyMembersKidneyDisease: values.familyMembersKidneyDisease,
            familyHistoryLiverDisease: i(values.familyHistoryLiverDisease),
            familyMembersLiverDisease: values.familyMembersLiverDisease,
            familyHistoryBowelIssues: i(values.familyHistoryBowelIssues),
            familyMembersBowelIssues: values.familyMembersBowelIssues,
            familyHistoryLupusLiver: i(values.familyHistoryLupusLiver),
            familyMembersLupusLiver: values.familyMembersLupusLiver,
            familyHistoryHeartDisease: i(values.familyHistoryHeartDisease),
            familyMembersHeartDisease: values.familyMembersHeartDisease,
            familyHistoryCad: i(values.familyHistoryCad),
            familyMembersCad: values.familyMembersCad,
            familyHistoryBloodPressure: i(values.familyHistoryBloodPressure),
            familyMembersBloodPressure: values.familyMembersBloodPressure,
            familyHistoryBloodDisorder: i(values.familyHistoryBloodDisorder),
            familyMembersBloodDisorder: values.familyMembersBloodDisorder,
            familyHistoryCancer: i(values.familyHistoryCancer),
            familyMembersCancer: values.familyMembersCancer,
            familyHistoryKidneyCancer: i(values.familyHistoryKidneyCancer),
            familyMembersKidneyCancer: values.familyMembersKidneyCancer,
            familyHistoryDiabetes: i(values.familyHistoryDiabetes),
            familyMembersDiabetes: values.familyMembersDiabetes,
            familyMemberSuddenDeathUnder50: i(values.familyMemberSuddenDeathUnder50),
            familyMemberSuddenDeathUnder50Description: values.familyMemberSuddenDeathUnder50Description,
        }),
        (nextStep) => ({ nextStep }),
    );

    return { model, isLoading, error, submit, isSubmitting, submitError };
};
